import { useTranslation } from 'react-i18next';
import Body from '../components/Body';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Typography } from '@mui/material';
import MarkdownText from '../components/MarkdownText';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import Image from '../components/Image';
import Section from '../components/Section';
import Video from '../components/Video';
import useLocalStorage from '../helpers/useLocalStorage';

export default function TrainingVideo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showedForm, setShowedForm] = useLocalStorage<string>('showedForm', 'false', true);
  const [showForm, setShowForm] = useState(showedForm === 'true');
  const [scrollForm, setScrollForm] = useState(false);

  const pageTitle = t('training.page_title', 'Hemligheten till kärleken: 4 steg till din drömpartner');
  const lead = t('training_video.lead', 'Se träningen nedan');
  const title = t('training.title', '4 steg till din drömpartner');
  const description = t('training.description', 'Lär dig våra professionella matchmakers BÄSTA tips om vad DU behöver göra för att hitta kärleken som du längtar efter och förtjänar.');

  const features = [
    {
      name: 'SVD',
      width: 132 / 2,
      icon: '/svd.png',
    },
    {
      name: 'Dagens Nyheter',
      width: 115 / 2,
      icon: '/dn.png',
    },
    {
      name: 'Elle',
      width: 138 / 2,
      icon: '/elle.png',
    },
    {
      name: 'Breakit',
      width: 205 / 2,
      icon: '/breakit.png',
    },
    {
      name: 'Financial Times',
      width: 464 / 2,
      icon: '/ft.png',
      fill: true,
    },
    {
      name: 'SVT',
      width: 112 / 2,
      icon: '/svt.png',
    },
    {
      name: 'DI Weekend',
      width: 211 / 2,
      icon: '/diweekend.png',
    },
    {
      name: 'Må Bra',
      width: 211 / 2,
      icon: '/mabra.png',
    },
    {
      name: 'BBC',
      width: 192 / 2,
      icon: '/bbc.png',
    },
    {
      name: 'Sveriges Radio',
      width: 350 / 2,
      icon: '/sr.png',
      fill: true,
    },
  ];

  const handleTimeUpdate = useCallback((currentTime: number) => {
    if (currentTime > 15 * 60) { // 15 minutes into the video
      setShowForm(true);
      setShowedForm('true');
    }
    if (currentTime > 35 * 60 + 6) {// 35.06 into the video
      setScrollForm(true);
    }
  }, [setShowForm]);

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (scrollForm) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      t = setTimeout(() => {
        const el = document.getElementById('form');
        if (el) {
          // center form in window.
          const top = el.getBoundingClientRect().top + window.scrollY - Math.min((window.innerHeight - el.offsetHeight) / 2, 80);
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 300);
    }
    return () => clearTimeout(t);
  }, [scrollForm]);

  const handleClick = useCallback(() => {
    navigate('/training-booking');
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: '#38191e',
      }}
    >
      <Helmet>
        <title>Relate | {pageTitle}</title>
      </Helmet>
      <Box sx={{
        display: 'flex',
        maxWidth: 800,
        margin: '0 auto',
      }}>        
        <Box
          sx={{
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 6 },
            pb: { xs: 8, sm: 12 },
            textAlign: 'center',
            em: { color: 'white', fontWeight: 'bold' },
          }}
        >
          <Image src="/logo.png" sx={{ width: { xs: 64, sm: 100 }, mb: { xs: 2, sm: 4 } }} />
          <Box sx={{ pb: 2 }}>
            <Typography component="h5" variant="h5" sx={{
              mb: 2, color: '#BF9197', textTransform: 'uppercase', fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: { xs: 20, md: 28 }
            }}>{lead}</Typography>
            <Typography component="h1" variant="h1" sx={{ mb: 3, textTransform: 'uppercase', fontSize: { md: 80 } }}>{title}</Typography>
            <Typography variant="body1">{description}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ background: '#9C4452', textAlign: 'center', p: 0.5 }}></Box>
      <Body>
        <Box>
          <Box
            id="form"
            sx={{
            mx: { xs: 2, md: 0 },
            position: 'relative',
            top: { xs: -48, sm: -64 },
            mb: -4,
          }}>
            <Video
              sx={{ my: 0 }}
              poster="https://embed-ssl.wistia.com/deliveries/ae896344b0cdd3c4269849ea4d1b1c8a.png"
              src={{
                m3u8: 'https://fast.wistia.com/embed/medias/lwr7575x2d.m3u8',
                caption: '/training-video.vtt',
              }}
              title={pageTitle}
              showTitle={false}
              onTimeUpdate={handleTimeUpdate}
            />
          </Box>
          <Section sx={{ display: showForm ? 'flex' : 'none', my: { xs: '16px !important', sm: 2 } }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              type="submit"
              onClick={handleClick}
              sx={{
                backgroundAttachment: 'fixed',
                backgroundImage:  'repeating-radial-gradient(circle at 400px -100px, #76451F66 0, transparent 800px), repeating-linear-gradient(#be8a6144, #be8a6122)',
                textTransform: 'uppercase',
              }}
            >
              {t('training_video.action', 'Boka ett gratis samtal med oss nu')}
            </Button>
          </Section>
          <Section sx={{ display: showForm ? 'flex' : 'none', backgroundColor: '#38191E', mx: '0px !important', px: { xs: 2, sm: 4 }, pt: 2, pb: 4, borderRadius: { xs: 0, sm: '32px !important' }, }}>
            <Typography component="h2" variant="h2" textAlign="center" sx={{ p: 2 }}>
              {t('training_video.quotes_title', 'Vad några av våra underbara klienter säger:')}
            </Typography>
            <Section sx={{ p: 2, mx: '0px !important', my: { xs: '16px !important', sm: '32px !important' }, backgroundColor: '#422327 !important', borderRadius: '24px !important', alignItems: 'center', flexDirection: 'row', }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" textAlign="center" fontStyle="italic" sx={{ mb: 2 }}>
                  {t('training_video.quote', 'Det känns så himla förstklassigt och otroligt bra stöd. Jag var inte beredd på alla de insikter som skulle komma från övningarna, jag känner mig typ som en helt ny människa. Mina kompisar säger att jag pratar på ett sätt som jag aldrig har gjort förut. Det har överträffat allt, det är så häftigt verkligen!')}
                </Typography>
                <Typography component="h5" variant="h5" textAlign="center">
                  {t('training_video.quote_author', '– {{name}}, {{age}} år', { name: 'Madeleine', age: 34 })}
                </Typography>
              </Box>
            </Section>
            <Section sx={{ p: 2, mx: '0px !important', my: { xs: '0px !important', sm: '16px !important' }, backgroundColor: '#422327 !important', borderRadius: '24px !important', alignItems: 'center', flexDirection: 'row' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" textAlign="center" fontStyle="italic" sx={{ mb: 2 }}>
                  {t('training_video.quote2', 'Jag förstår nu att personerna jag dejtade innan programmet var så långt från vem jag faktiskt söker. Denna upplevelse har hjälpt mig att inse vem jag verkligen behöver och vill ha i mitt liv. Jag har uppskattat min matchmakers lyhördhet för vad jag vill ha samtidigt som jag har blivit utmanad i det. Jag tyckte att varje introduktion kändes närmare och närmare den rätta och idag träffar jag en tjej där jag känner att det finns något att bygga en framtid på.')}
                </Typography>
                <Typography component="h5" variant="h5" textAlign="center">
                  {t('training_video.quote2_author', '– {{name}}, {{age}} år', { name: 'Emil', age: 36 })}
                </Typography>
              </Box>
            </Section>
          </Section>
          <Section sx={{ display: showForm ? 'flex' : 'none' }}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography component="h2" variant="h2" textAlign="center" sx={{ mb: 1 }}>
                {t('training_video.sales_title', 'Låter det fantastiskt?')}
              </Typography>
              <Typography component="h2" variant="h2" textAlign="center" sx={{ mb: 3 }}>
                {t('training_video.sales_sub_title', 'Vi är här för att hjälpa dig med!')}
              </Typography>
              <MarkdownText>
                {t('training_video.sales', `Vi vet att det idag är otroligt många singlar som är trötta på dejting-apparna och som verkligen vill träffa sin person som de kan bygga en framtiden med.\n\nTillsammans med Skandinaviens ledande och en av världens bästa matchmakingbyråer har vi tagit fram ett 6 månaders program som hjälper DIG att äntligen hitta kärleken.\n\nVi har redan hjälpt tusentals singlar och vi kan hjälpa dig med samma sak.`)}
              </MarkdownText>
              <Image src="/lemarc-team.png" sx={{ width: 'calc(100% + 48px)', mx: -3, mt: 2, mb: 3 }} />
              <MarkdownText>
                {t('training_video.sales_desc', `I vårt matchmaking-program tar vi dig personligen steg-för-steg till kärleken genom att introducera handplockade personer som är rätt för just DIG. \n\nHur gör vi detta?\n\nFörst sätter vi grunden för din kommande kärlek och partnersökning genom att förstå ditt förflutna och förstå dina tidigare relationer. Vi har en metod för exakt hur vi gör det här. Tillsammans bearbetar vi det som varit för att skapa mening, förstå dig och vad du behöver. Insikterna som kommer från det här steget är avgörande och gör att du kan skapa kärlek som håller livet ut.\n\nEfter det lär vi dig leva som ditt mest autentiska jag. Här går vi på djupet för att förstå vad som verkligen är viktigt för dig i livet och en relation.\n\nNär vi har gjort det så identifierar vi tillsammans din framtida drömrelation och person. Med insikterna från tidigare steg kan vi skapa en tydlig helhetsbild av precis vad du söker och behöver i en partner.\n\nOch till sist så sållar din personliga matchmaker igenom tusentals potentiella matchningar åt dig. Vi hittar de mest lovande matchningarna och introducerar de allra bästa för dig. Här planerar vi in dejter där din bästa sida kommer fram och hjälper till att förbereda dig för att lyckas på bästa sätt.\n\nVi samlar feedback från alla dina dejter för att ge dig en ärlig och realistisk bild av hur du uppfattas vilket hjälper både oss och dig när vi fortsätter sökandet efter fler matcher tills att vi hittar den rätta.\n\nUnder hela programmet har du din egna personliga matchmaker vid din sida. En kärleksexpert som stöttar, ger råd, pushar och finns där när det behövs. \n\nSå sluta att slösa flera år på att försöka göra allt detta på egen hand.\n\n*Du har två alternativ:*\n\nFortsätta att drömma om kärleken, personen och relationen du vill ha.\n\nFortsätta testa olika appar som inte vill att du ska lyckas.\n\nOch lägga flera år på att dejta fel personer på fel sätt.\n\nSanningen är att du då riskerar att lämna kärleken åt slumpen.\n\n*Eller så kan du SLUTA att gissa dig fram.*\n\nMed vårt program får du expertvägledningen som tar dig på den snabbaste vägen till din drömrelation.\n\nDet här är inget som är nytt för oss. Det här är vad vi gör varje dag.\n\nDu är här av en anledning. Så varför inte ta nästa bästa steg för att se till att du får den kärleken du förtjänar i ditt liv?\n\nI vårt matchmaking-program tar vi dig igenom en bevisad och tydlig process som fungerar.\n\nAtt fråga om detta kommer att fungera för dig är fel fråga – processen funkar och vi kan hjälpa dig.\n\nFrågan är om du är redo att satsa på kärleken?\n\nKlicka i såna fall på knappen nedan och boka ett gratis utforskande samtal med vårt team för att se om den här möjligheten passar dig.`)}
              </MarkdownText>
              <Typography component="h2" variant="h2" textAlign="center" sx={{ mt: 6, pt: 4, borderTop: 'solid 1px #38191E' }}>
                {t('training_video.sales_final_title', 'Vad väntar du på?')}
              </Typography>
            </Box>
            <Button
              fullWidth
              size="large"
              variant="contained"
              type="submit"
              onClick={handleClick}
              sx={{
                my: 1,
                backgroundAttachment: 'fixed',
                backgroundImage:  'repeating-radial-gradient(circle at 400px -100px, #76451F66 0, transparent 800px), repeating-linear-gradient(#be8a6144, #be8a6122)',
                textTransform: 'uppercase',
              }}
            >
              {t('training_video.sales_action', 'Boka ett gratis samtal med oss nu')}
            </Button>
          </Section>
          <Section sx={{ backgroundColor: '#38191E !important', mx: '0px !important', px: { xs: 2, sm: 4 }, py: 4 }}>
            <Typography component="h6" variant="h6" textAlign="center" textTransform="uppercase" fontStyle="italic" fontFamily="'DM Sans', sans-serif">
              {t('featured_in.title', 'Featured i')}
            </Typography>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
            }}>
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', maxWidth: 320, margin: '0 auto' }}>
                {features.slice(0, 5).map((item) => (
                  <Box
                    key={item.name} 
                    sx={{
                      flex: 1,
                      flexBasis: item?.fill ? '100%' : '50%',
                      width: item?.fill ? '100%' : '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 80,
                    }}
                  >
                    <Box
                      component="img"
                      src={item.icon}
                      sx={{
                        width: item.width,
                      }}
                      title={item.name}
                    />
                  </Box>
                ))}
              </Box>
              <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', maxWidth: 320, margin: '0 auto' }}>
                {features.slice(5).map((item) => (
                  <Box
                    key={item.name} 
                    sx={{
                      flex: 1,
                      flexBasis: item?.fill ? '100%' : '50%',
                      width: item?.fill ? '100%' : '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 80,
                    }}
                  >
                    <Box
                      component="img"
                      src={item.icon}
                      sx={{
                        width: item.width,
                      }}
                      title={item.name}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          </Section>
          <Section sx={{ alignItems: 'center' }}>
            <Image src="/logo.png" sx={{ width: 84 }} />
            <Typography variant="caption">
              {t('slogan')}
            </Typography>
          </Section>
          <Section />
        </Box>
      </Body>
    </Box>
  );
}
